import { db, type DraftEntry } from './offlineDb';

const randomUUID = () => crypto.randomUUID();

export const offlineStorage = {
  async saveDraft(
    userId: string,
    claimId: string,
    type: 'mileage' | 'petty-cash',
    data: Record<string, unknown>,
    localId?: string,
    serverId?: string,
  ): Promise<string> {
    const id = localId ?? randomUUID();
    const now = new Date();

    const existing = localId
      ? await db.drafts.where('localId').equals(localId).first()
      : null;

    if (existing?.id) {
      await db.drafts.update(existing.id, {
        data,
        userId,
        syncStatus: 'pending',
        retryCount: 0,
        updatedAt: now,
        ...(serverId && { serverId }),
      });
    } else {
      await db.drafts.add({
        localId: id,
        serverId,
        userId,
        claimId,
        type,
        data,
        syncStatus: 'pending',
        retryCount: 0,
        deleted: false,
        createdAt: now,
        updatedAt: now,
      });
    }

    return id;
  },

  async getDraftsForClaim(userId: string, claimId: string): Promise<DraftEntry[]> {
    return db.drafts
      .where('claimId')
      .equals(claimId)
      .filter((d) => !d.deleted && d.userId === userId)
      .toArray();
  },

  async markSynced(localId: string, serverId: string): Promise<void> {
    const draft = await db.drafts.where('localId').equals(localId).first();
    if (draft?.id) {
      await db.drafts.update(draft.id, { serverId, syncStatus: 'synced', retryCount: 0 });
    }
  },

  async markError(localId: string, recoverable = true): Promise<void> {
    const draft = await db.drafts.where('localId').equals(localId).first();
    if (draft?.id) {
      const retryCount = (draft.retryCount ?? 0) + 1;
      await db.drafts.update(draft.id, {
        syncStatus: recoverable && retryCount < 8 ? 'pending' : 'error',
        retryCount,
      });
    }
  },

  async deleteDraft(localId: string): Promise<void> {
    const draft = await db.drafts.where('localId').equals(localId).first();
    if (draft?.id) {
      await db.drafts.update(draft.id, { deleted: true, syncStatus: 'pending' });
    }
  },

  async getPendingEntries(userId: string): Promise<DraftEntry[]> {
    return db.drafts
      .where('syncStatus')
      .equals('pending')
      .filter((d) => d.userId === userId)
      .toArray();
  },

  async clearSyncedDrafts(claimId: string): Promise<void> {
    const synced = await db.drafts
      .where('claimId')
      .equals(claimId)
      .filter((d) => d.syncStatus === 'synced')
      .toArray();

    const ids = synced.map((d) => d.id!).filter(Boolean);
    if (ids.length > 0) {
      await db.drafts.bulkDelete(ids);
    }
  },
};
